import React, {useCallback, useMemo, useState} from 'react';
import {Theme} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Button from 'components/Button';
import Paper from '@mui/material/Paper';
import {useTranslation} from 'react-i18next';
import Typography from '@mui/material/Typography';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';
import {readOnlyContext} from 'common/context/ReadOnlyContext';
import {baseNameComparator, transferListComparator} from 'common/stringUtils';
import {compact} from 'lodash';
import TextField from './TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ClearIcon from '@mui/icons-material/Clear';

type Option = {id: string; name: string};

const useStyles = makeStyles((theme: Theme) =>
   createStyles({
      root: {
         margin: 'auto',
         marginBottom: theme.spacing(4),
      },
      paper: {
         width: 275,
         height: 230,
         overflow: 'auto',
         borderColor: theme.palette.primary.main,
      },
      button: {
         margin: theme.spacing(0.5, 0),
      },
      list: {
         textAlign: 'center',
      },
   })
);

function not(a: string[], b: string[]) {
   return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: string[], b: string[]) {
   return a.filter((value) => b.indexOf(value) !== -1);
}

const CustomList = ({
   items,
   label,
   options,
   checked,
   handleToggle,
   disabled,
   freeze,
}: {
   items: string[];
   label: string;
   options: Option[];
   checked: string[];
   handleToggle: (v: string) => () => void;
   disabled?: boolean;
   freeze?: string[];
}) => {
   const classes = useStyles();
   if (!options) return <>No options</>;

   const _values = compact(items.map((i) => options?.find((o) => o.id === i)));
   const values = _values.sort(freeze ? transferListComparator(freeze) : baseNameComparator);
   // console.debug('customList', values, options, items);
   return (
      <>
         <Typography variant={'body2'} gutterBottom fontWeight="bold">
            {label}
         </Typography>
         <Paper variant={'outlined'} className={classes.paper}>
            <List dense component="div" role="list">
               {values.map((option) => {
                  const value = option.id;
                  const labelId = `transfer-list-item-${value}-label`;
                  return (
                     <ListItem
                        key={value}
                        role="listitem"
                        button
                        disabled={
                           disabled || (freeze && option && freeze.indexOf(option.name) >= 0)
                        }
                        onClick={handleToggle(value)}
                     >
                        <ListItemIcon>
                           <Checkbox
                              checked={checked.indexOf(value) !== -1}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{'aria-labelledby': labelId}}
                           />
                        </ListItemIcon>
                        <ListItemText id={labelId} primary={option?.name} />
                     </ListItem>
                  );
               })}
               <ListItem />
            </List>
         </Paper>
      </>
   );
};

export type FormikTransferListSearchableProps = {
   name: string;
   valuesLabel: string;
   optionsLabel: string;
   options: Option[];
   freeze?: string[];
   inverse?: boolean;
};

const FormikTransferListSearchable = ({
   name,
   valuesLabel: _leftLabel,
   optionsLabel: _rightLabel,
   options,
   freeze,
   inverse,
}: FormikTransferListSearchableProps) => {
   const classes = useStyles();
   const {t} = useTranslation();
   const disabled = React.useContext(readOnlyContext);
   // const [field, , helpers] = useField<string[]>({name, multiple: true});
   const {
      field,
      formState,
      fieldState: {error},
   } = useController({name, defaultValue: []});
   const {setValue} = useFormContext();

   const [checked, setChecked] = React.useState<string[]>([]);
   const [_left, setLeft] = React.useState<string[]>(() => field.value);
   const [_right, setRight] = React.useState<string[]>(() =>
      options
         ? not(
              options.map((option) => option.id),
              field.value
           )
         : []
   );
   // console.debug('options', options, left, right);

   // Reset
   React.useEffect(() => {
      if (!formState.isDirty && options) {
         console.debug('reset', formState);
         setRight(
            not(
               options.map((option) => option.id),
               field.value
            )
         );
         setLeft(field.value);
      }
   }, [formState.isDirty, field.value, options]);

   const handleSetValue = (values: string[]) => {
      // console.debug('setvalue', name, values);
      setValue(name, values, SetValueOptions);
   };

   const leftChecked = intersection(checked, _left);
   const rightChecked = intersection(checked, _right);
   // const lastRightCheckedIndex = right.indexOf(rightChecked[rightChecked.length - 1]);

   const handleToggle = (value: string) => () => {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];

      if (currentIndex === -1) {
         newChecked.push(value);
      } else {
         newChecked.splice(currentIndex, 1);
      }

      setChecked(newChecked);
   };

   const handleCheckedRight = () => {
      setRight(_right.concat(leftChecked));
      const val = not(_left, leftChecked);
      setLeft(val);
      setChecked(not(checked, leftChecked));
      handleSetValue(val);
   };

   const handleCheckedLeft = () => {
      const val = _left.concat(rightChecked);
      setLeft(val);
      setRight(not(_right, rightChecked));
      setChecked(not(checked, rightChecked));
      handleSetValue(val);
   };
   // const handleMoveUp = () => {
   //    setRight((right) => {
   //       return swapArrayElements(right, lastRightCheckedIndex - 1, lastRightCheckedIndex);
   //    });
   //    handleSetValue();
   // };
   // const handleMoveDown = () => {
   //    setRight((right) => {
   //       return swapArrayElements(right, lastRightCheckedIndex, lastRightCheckedIndex + 1);
   //    });
   //    handleSetValue();
   // };

   const left = inverse ? _right : _left;
   const right = inverse ? _left : _right;
   const leftLabel = inverse ? _rightLabel : _leftLabel;
   const rightLabel = inverse ? _leftLabel : _rightLabel;

   const [filter, setFilter] = useState('');
   const filtered = useMemo(() => {
      const _filter = filter.toLocaleLowerCase();
      const _filtered = filter
         ? options.filter((item) => item.name.toLocaleLowerCase().indexOf(_filter) >= 0)
         : options;
      return _filtered;
   }, [filter, setFilter, options]);

   return (
      <>
         <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="start"
            className={classes.root}
         >
            <Grid item className={classes.list} flex={0}>
               <CustomList
                  items={left}
                  label={leftLabel}
                  freeze={freeze}
                  disabled={disabled}
                  options={filtered}
                  handleToggle={handleToggle}
                  checked={checked}
               />
               {/* {customList(filtered, leftLabel, freeze)} */}
               <TextField
                  value={filter}
                  onChange={(ev) => {
                     setFilter(ev.target.value);
                  }}
                  label={t('Search')}
                  fullWidth
                  InputProps={{
                     endAdornment: (
                        <InputAdornment position="end">
                           <IconButton
                              onClick={() => {
                                 setFilter('');
                              }}
                              aria-label="Clear"
                              edge="end"
                           >
                              <ClearIcon />
                           </IconButton>
                        </InputAdornment>
                     ),
                  }}
               />
            </Grid>
            <Grid item alignSelf="center">
               <Grid container direction="column" alignItems="center">
                  <Button
                     variant="outlined"
                     size="small"
                     className={classes.button}
                     onClick={handleCheckedLeft}
                     disabled={disabled || rightChecked.length === 0}
                     aria-label="move selected left"
                  >
                     {inverse ? '>' : '<'}
                  </Button>
                  <Button
                     variant="outlined"
                     size="small"
                     className={classes.button}
                     onClick={handleCheckedRight}
                     disabled={disabled || leftChecked.length === 0}
                     aria-label="move selected right"
                  >
                     {inverse ? '<' : '>'}
                  </Button>
                  {/* <Button
                     variant="outlined"
                     size="small"
                     className={classes.button}
                     onClick={handleMoveUp}
                     disabled={rightChecked.length !== 1 || lastRightCheckedIndex === 0}
                     aria-label="move selected up"
                  >
                     &#129045;
                  </Button>
                  <Button
                     variant="outlined"
                     size="small"
                     className={classes.button}
                     onClick={handleMoveDown}
                     disabled={
                        rightChecked.length !== 1 || lastRightCheckedIndex === right.length - 1
                     }
                     aria-label="move selected down"
                  >
                     &#129047;
                  </Button> */}
               </Grid>
            </Grid>
            <Grid item className={classes.list}>
               <CustomList
                  items={right}
                  label={rightLabel}
                  freeze={freeze}
                  disabled={disabled}
                  options={options}
                  handleToggle={handleToggle}
                  checked={checked}
               />
               {/* {customList(right, rightLabel, freeze)} */}
            </Grid>
            {error?.message && (
               <Grid xs={12} item>
                  <Typography color="error" textAlign="center">
                     {error?.message}
                  </Typography>
               </Grid>
            )}
         </Grid>
      </>
   );
};

export default FormikTransferListSearchable;
